import { fitWithin } from '../utils/compressImage';

// A shared story board is a masonry of photos, not a print file. 1600 px
// covers a retina column, and ~400 KB is about a tenth of a 4 MB phone photo.
export const GALLERY_MAX_EDGE = 1600;
export const GALLERY_MIN_EDGE = 1200;
export const GALLERY_TARGET_BYTES = 400 * 1024;
export const GALLERY_START_QUALITY = 0.8;
export const GALLERY_MIN_QUALITY = 0.64;

const PASS_THROUGH = ['image/gif', 'image/svg+xml'];

export const shouldPassThroughGallery = (file) => {
    if (!file || !file.type || !file.type.startsWith('image/')) return true;
    return PASS_THROUGH.includes(file.type);
};

export const initialGalleryPass = (width, height) => {
    const longest = Math.max(width || 0, height || 0);
    return {
        edge: Math.min(GALLERY_MAX_EDGE, longest || GALLERY_MAX_EDGE),
        quality: GALLERY_START_QUALITY,
    };
};

// Keep stepping down quality, then the long edge, until the file is near the
// size budget. Returns null when this pass is good enough or the floor is hit.
export const nextGalleryPass = (pass, blobBytes) => {
    if (blobBytes <= GALLERY_TARGET_BYTES) return null;
    if (pass.quality > GALLERY_MIN_QUALITY + 0.001) {
        return {
            edge: pass.edge,
            quality: Math.max(GALLERY_MIN_QUALITY, Math.round((pass.quality - 0.08) * 100) / 100),
        };
    }
    if (pass.edge > GALLERY_MIN_EDGE) {
        return {
            edge: Math.max(GALLERY_MIN_EDGE, Math.round(pass.edge * 0.85)),
            quality: GALLERY_START_QUALITY,
        };
    }
    return null;
};

const loadImageElement = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, revoke: () => URL.revokeObjectURL(url) });
    img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Image could not be decoded'));
    };
    img.src = url;
});

const canvasToBlob = (canvas, quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
});

const encodeJpeg = (img, width, height, quality) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return Promise.resolve(null);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
    return canvasToBlob(canvas, quality);
};

export async function shrinkToTarget(encode, { width, height }) {
    let pass = initialGalleryPass(width, height);
    let best = null;
    for (let attempt = 0; attempt < 10; attempt++) {
        const size = fitWithin(width, height, pass.edge);
        if (!size.width || !size.height) break;
        const blob = await encode(size.width, size.height, pass.quality);
        if (!blob) break;
        best = blob;
        const next = nextGalleryPass(pass, blob.size);
        if (!next) break;
        pass = next;
    }
    return best;
}

const asOriginal = (file) => ({
    blob: file,
    type: file?.type || '',
    name: file?.name || 'image',
});

/**
 * Resize and re-encode a story upload before it goes to Storage.
 * GIFs stay untouched so the animation survives. Anything that fails to
 * decode is uploaded as-is.
 *
 * @param {File} file
 * @param {{ load?: Function, encode?: Function }} [deps] test seams
 * @returns {Promise<{ blob: Blob, type: string, name: string }>}
 */
export async function compressGalleryFile(file, deps = {}) {
    if (shouldPassThroughGallery(file)) return asOriginal(file);

    const load = deps.load || loadImageElement;
    const encode = deps.encode || encodeJpeg;
    let loaded;
    try {
        loaded = await load(file);
        const img = loaded.img;
        const width = img?.naturalWidth;
        const height = img?.naturalHeight;
        if (!width || !height) return asOriginal(file);

        const blob = await shrinkToTarget(
            (w, h, quality) => encode(img, w, h, quality),
            { width, height }
        );
        if (!blob || blob.size >= file.size) return asOriginal(file);

        const base = (file.name || 'image').replace(/\.[^.]+$/, '');
        return { blob, type: 'image/jpeg', name: `${base}.jpg` };
    } catch (e) {
        console.warn('Image compression failed, uploading original:', file?.name, e);
        return asOriginal(file);
    } finally {
        if (loaded && loaded.revoke) loaded.revoke();
    }
}
